import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, CircularProgress, IconButton, Avatar } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

const WEEKDAY_LABELS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_NAME_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ── Shared thin, themed scrollbar (used anywhere content can overflow) ──
// Purely cosmetic — replaces the default chunky browser scrollbar with a
// slim rounded thumb that matches the rest of the widget's palette.
const thinScrollbarSx = {
  scrollbarWidth: "thin", // Firefox
  scrollbarColor: "#D8D5E0 transparent", // Firefox
  "&::-webkit-scrollbar": { width: "5px", height: "5px" }, // Chrome/Safari/Edge
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#D8D5E0",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#B9B4C8" },
};

// Both the weekday-label header row and the day-number grid MUST share this
// exact column template, or the two rows drift out of alignment (e.g. dates
// creeping one column to the right of their weekday label — see bug report).
// 7 equal columns, one per day of the week — no extra leading column.
const CALENDAR_GRID_COLUMNS = "repeat(7, 1fr)";

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// 1 -> "1st", 2 -> "2nd", 3 -> "3rd", 4 -> "4th" ... (for the "Fri 28th" header)
const ordinalSuffix = (n) => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return "th";
  switch (n % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
};

// Formats a time as Cal.com does: lowercase am/pm, no space, no leading
// zero on the hour (e.g. "10:00am", "1:00pm"). We deliberately don't use
// toLocaleTimeString() here — its output (spacing, case, leading zeros)
// depends on the browser/OS locale, which is why the same code can render
// "10:00am" on one machine and "04:30 PM" on another.
const formatTimeLabel = (isoStr, is24h) => {
  const d = new Date(isoStr);
  const minutes = String(d.getMinutes()).padStart(2, "0");
  if (is24h) {
    return `${String(d.getHours()).padStart(2, "0")}:${minutes}`;
  }
  const period = d.getHours() >= 12 ? "pm" : "am";
  const hour12 = d.getHours() % 12 || 12;
  return `${hour12}:${minutes}${period}`;
};

// Builds a 6-row (42 cell) month grid, Sunday-first, including the
// leading/trailing days from adjacent months (like Cal.com / Google Cal).
const buildMonthGrid = (year, month) => {
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = firstOfMonth.getDay(); // 0=Sun
  const gridStart = new Date(year, month, 1 - startOffset);

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
};

const MAX_MONTHS_AHEAD = 12;

const GoogleSlotPicker = ({
  recruiterEmail,
  candidateName,
  candidateEmail,
  submissionId,
  onBooked,
  // ── NEW (optional) profile-panel props — all have safe defaults, so
  // existing call sites that don't pass these keep working unchanged. ──
  recruiterName,
  recruiterAvatarUrl,
  eventTitle = "Stop Losing A-Players to Slow Recruitment Processes",
  eventDescription = "How to make offers in 7 days (not 7 weeks)\n The exact Getgeek features that'll 10x your hiring speed\nThis call is for recruiters and hiring managers who are tired of losing great candidates to faster companies.\nIf that's you, grab a slot below.",
  durationMinutes = 30,
  meetingProvider = "Google Meet",
  timezoneLabel,
}) => {
  const today = useMemo(() => startOfDay(new Date()), []);

  // Fallback: derive a display name from the recruiter's email if none given.
  const displayRecruiterName = useMemo(() => {
    if (recruiterName) return recruiterName;
    if (!recruiterEmail) return "";
    const local = recruiterEmail.split("@")[0] || "";
    return local
      .replace(/[._-]+/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }, [recruiterName, recruiterEmail]);

  const displayTimezone = useMemo(() => {
    if (timezoneLabel) return timezoneLabel;
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "";
    }
  }, [timezoneLabel]);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(today);
  const [is24h, setIs24h] = useState(false); // display-only toggle, doesn't touch booking payload
  // Mobile-only (below the "md" row layout) step control: which of the two
  // stacked panels — the calendar or the time list — is currently shown.
  // On md+ (row layout) both panels are always visible side by side and
  // this value is simply ignored.
  const [mobileStep, setMobileStep] = useState("calendar"); // "calendar" | "time"

  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState("");

  const monthCells = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);
  const dateStr = toDateStr(selectedDate);

  const minMonth = today.getFullYear() * 12 + today.getMonth();
  const maxMonth = minMonth + MAX_MONTHS_AHEAD;
  const currentMonthIdx = viewYear * 12 + viewMonth;

  const goPrevMonth = () => {
    if (currentMonthIdx <= minMonth) return;
    const m = viewMonth === 0 ? 11 : viewMonth - 1;
    const y = viewMonth === 0 ? viewYear - 1 : viewYear;
    setViewMonth(m);
    setViewYear(y);
  };

  const goNextMonth = () => {
    if (currentMonthIdx >= maxMonth) return;
    const m = viewMonth === 11 ? 0 : viewMonth + 1;
    const y = viewMonth === 11 ? viewYear + 1 : viewYear;
    setViewMonth(m);
    setViewYear(y);
  };

  useEffect(() => {
    let cancelled = false;
    setLoadingSlots(true);
    setError("");
    setSlots([]);

    fetch(
      `/api/google/availability?recruiterEmail=${encodeURIComponent(
        recruiterEmail,
      )}&date=${dateStr}`,
    )
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.success) setSlots(data.slots);
        else setError(data.message || "Slots could not be loaded");
      })
      .catch(() => {
        if (!cancelled) setError("Slots could not be loaded. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dateStr, recruiterEmail]);

  const handleBook = async (slot) => {
    setBooking(true);
    setError("");
    try {
      const res = await fetch("/api/google/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recruiterEmail,
          startTime: slot.start,
          endTime: slot.end,
          candidateName,
          candidateEmail,
          submissionId,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || "Booking fail ho gayi.");
        setBooking(false);
        return;
      }
      onBooked?.({ startTime: data.startTime, endTime: data.endTime, meetLink: data.meetLink });
    } catch {
      setError("Booking failed. Please try again.");
      setBooking(false);
    }
  };

  return (
    <Box
      // direction: "ltr" is forced on the whole widget (not just the
      // calendar) so the three panels always render in a fixed
      // Profile → Calendar → Time-slots order, left to right — even when
      // an RTL (e.g. Arabic) parent wraps this component. Without this,
      // the parent's inherited `direction: rtl` flips the flex order of
      // the panels themselves (Time-slots ends up on the left), not just
      // the calendar grid inside them.
      dir="ltr"
      sx={{
        width: "100%",
        height: "80vh",
        display: "flex",
        direction: "ltr",
        // Row layout only kicks in once there's genuinely enough width for
        // all three panels (~900px+). Below that (xs/sm/most tablets in the
        // popup) it stacks vertically so nothing gets squeezed or clipped.
        flexDirection: { xs: "column", md: "row" },
        fontFamily: "var(--font-outfit), sans-serif",
        bgcolor: "#fff",
        borderRadius: { xs: "12px", sm: "16px" },
        border: "1px solid #F1F1F4",
        boxShadow: "0 12px 32px rgba(16, 24, 40, 0.10)",
        // On stacked (mobile/tablet) layout the three sections combined can
        // be taller than the widget's allotted height (the parent modal
        // clips overflow), so the widget itself needs to scroll vertically
        // or the time-slots panel at the bottom becomes invisible. In the
        // md+ row layout there's no stacking, so this scroll never engages.
        overflowY: { xs: "auto", md: "hidden" },
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        ...thinScrollbarSx,
      }}
    >
      {/* ── LEFT: Profile / event info panel ── */}
      <Box
        sx={{
          flex: { xs: "0 0 auto", md: "0 0 280px" },
          borderRight: { xs: "none", md: "1px solid #EDEDF1" },
          borderBottom: { xs: "1px solid #EDEDF1", md: "none" },
          p: { xs: 1.75, sm: 2, md: 2.5 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Avatar + recruiter name now sit side-by-side on the same line */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: { xs: 1.75, md: 2.25 },
          }}
        >
          <Avatar
            src={recruiterAvatarUrl || undefined}
            sx={{
              width: 40,
              height: 40,
              bgcolor: "#EDE9FE",
              color: "#7C3AED",
              fontWeight: 700,
              fontSize: "1rem",
              flexShrink: 0,
            }}
          >
            {/* Always provide the initial-letter fallback (not just when no
                avatarUrl was given) — MUI's Avatar automatically prefers the
                image and only renders these children if the image fails to
                load. Without this, a broken/placeholder image URL falls back
                to MUI's generic person-silhouette icon instead of a letter. */}
            {displayRecruiterName ? displayRecruiterName.charAt(0).toUpperCase() : null}
          </Avatar>

          {displayRecruiterName && (
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#374151" }}>
              {displayRecruiterName}
            </Typography>
          )}
        </Box>

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1rem", sm: "1.05rem", md: "1.1rem" },
            color: "#111827",
            lineHeight: 1.35,
            mb: { xs: 1.25, md: 2 },
            fontFamily: "var(--font-plusjakarta), sans-serif",
            mt:'2%'
          }}
        >
          {eventTitle}
        </Typography>

        {eventDescription && (
          <Box
            sx={{
              position: "relative",
              maxHeight: { xs: 140, md: 220 },
              mb: { xs: 2, md: 2.5 },
              // Soft fade at the top/bottom edges hints there's more content
              // to scroll, Cal.com-style, instead of a hard cutoff.
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent 0, black 12px, black calc(100% - 12px), transparent 100%)",
              maskImage:
                "linear-gradient(to bottom, transparent 0, black 12px, black calc(100% - 12px), transparent 100%)",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: "#9CA3AF",
                whiteSpace: "pre-line",
                lineHeight: 1.5,
                maxHeight: { xs: 140, md: 220 },
                overflowY: "auto",
                pr: 0.75,
                py: "12px",
                // Fully hide the scrollbar in every browser — the fade
                // mask above is the only scroll affordance, matching the
                // reference (no visible track/thumb/arrow buttons at all).
                scrollbarWidth: "none", // Firefox
                msOverflowStyle: "none", // old Edge/IE
                "&::-webkit-scrollbar": { width: 0, height: 0, display: "none" }, // Chrome/Safari/Edge
              }}
            >
              {eventDescription}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            mt: { xs: 0, md: "auto" },
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            flexWrap: "wrap",
            gap: { xs: 1.5, md: 1.4 },
            pt: { xs: 0, md: 2 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#374151" }}>
            <AccessTimeRoundedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
            <Typography sx={{ fontSize: "0.85rem" }}>{durationMinutes}m</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#374151" }}>
            <VideocamRoundedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
            <Typography sx={{ fontSize: "0.85rem" }}>{meetingProvider}</Typography>
          </Box>
          {displayTimezone && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#374151", cursor: "default" }}>
              <LanguageRoundedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
              <Typography sx={{ fontSize: "0.85rem" }}>{displayTimezone}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── LEFT-CENTER: Month calendar ──
          direction: "ltr" is forced here (and doesn't cascade in from the
          parent) so the calendar always starts from the left — SUN...SAT
          left-to-right, dates in the same order — even when the rest of
          the form is rendered RTL (e.g. Arabic). CSS Grid auto-placement
          otherwise follows the inherited `direction` and would silently
          mirror the header/day columns in RTL contexts. */}
      <Box
        sx={{
          display: { xs: mobileStep === "time" ? "none" : "block", md: "block" },
          flex: { xs: "0 0 auto", md: "1 1 340px" },
          minWidth: { md: 300 },
          borderRight: { xs: "none", md: "1px solid #EDEDF1" },
          borderBottom: { xs: "1px solid #EDEDF1", md: "none" },
          p: { xs: 1.75, sm: 2, md: 2.5 },
          direction: "ltr",
        }}
      >
        {/* Month header + nav */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: { xs: 1.5, md: 2 } }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "0.95rem", sm: "1rem", md: "1.05rem" },
              color: "#111827",
              fontFamily: "var(--font-plusjakarta), sans-serif",
            }}
          >
            {MONTH_LABELS[viewMonth]} <Box component="span" sx={{ color: "#9CA3AF", fontWeight: 500 }}>{viewYear}</Box>
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={goPrevMonth}
              disabled={currentMonthIdx <= minMonth}
              sx={{
                color: "#9CA3AF",
                bgcolor: "#FAFAFB",
                "&:hover": { bgcolor: "#F0F0F3", color: "#374151" },
                "&.Mui-disabled": { color: "#E5E7EB", bgcolor: "transparent" },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={goNextMonth}
              disabled={currentMonthIdx >= maxMonth}
              sx={{
                color: "#9CA3AF",
                bgcolor: "#FAFAFB",
                "&:hover": { bgcolor: "#F0F0F3", color: "#374151" },
                "&.Mui-disabled": { color: "#E5E7EB", bgcolor: "transparent" },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Weekday header row — MUST use the same column template as the
            day grid below (CALENDAR_GRID_COLUMNS) or the labels drift out
            of alignment with the actual date cells. */}
        <Box sx={{ display: "grid", gridTemplateColumns: CALENDAR_GRID_COLUMNS, mb: 1 }}>
          {WEEKDAY_LABELS.map((w) => (
            <Typography
              key={w}
              sx={{
                textAlign: "center",
                fontSize: { xs: "0.62rem", sm: "0.65rem", md: "0.68rem" },
                fontWeight: 700,
                letterSpacing: "0.03em",
                color: "#B0AFB8",
                py: 0.5,
              }}
            >
              {w}
            </Typography>
          ))}
        </Box>

        {/* Day grid — same CALENDAR_GRID_COLUMNS as the header above, so
            each date sits directly under its correct weekday label. */}
        <Box sx={{ display: "grid", gridTemplateColumns: CALENDAR_GRID_COLUMNS, gap: { xs: "4px", sm: "5px", md: "6px" } }}>
          {monthCells.map((d) => {
            const inCurrentMonth = d.getMonth() === viewMonth;
            const isPast = d < today;
            const isToday = isSameDay(d, today);
            const isSelected = isSameDay(d, selectedDate);
            const disabled = isPast || !inCurrentMonth;
            // Visual "available" look for clickable future dates in the
            // current month (matches the light-gray cells in the reference
            // design) — purely cosmetic, no month-wide availability fetch.
            const looksAvailable = !disabled && !isSelected;

            return (
              <Box
                key={d.toISOString()}
                onClick={() => {
                  if (disabled) return;
                  setSelectedDate(startOfDay(d));
                  setMobileStep("time");
                }}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: { xs: 30, sm: 33, md: 36 },
                  borderRadius: "10px",
                  cursor: disabled ? "default" : "pointer",
                  fontSize: { xs: "0.78rem", sm: "0.82rem", md: "0.85rem" },
                  fontWeight: isSelected ? 700 : 500,
                  color: disabled ? "#D1D5DB" : isSelected ? "#fff" : "#374151",
                  bgcolor: isSelected ? "#4C1D95" : looksAvailable ? "#F6F6F8" : "transparent",
                  transition: "background-color 0.15s ease",
                  "&:hover": !disabled && !isSelected ? { bgcolor: "#EAEAEE" } : {},
                }}
              >
                {d.getDate()}
                {isToday && !isSelected && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 5,
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      bgcolor: "#7C3AED",
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── RIGHT: Selected date + time slots ── */}
      <Box
        sx={{
          display: { xs: mobileStep === "calendar" ? "none" : "flex", md: "flex" },
          flex: { xs: "1 1 auto", md: "0 0 260px" },
          minWidth: 0,
          minHeight: { xs: 260, md: 0 },
          p: { xs: 1.75, sm: 2, md: 2.5 },
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: { xs: 1.25, md: 1.75 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {/* Back-to-calendar control — mobile/stacked layout only. On
                md+ both panels are already visible side by side, so there's
                nothing to go "back" to and the button is hidden. */}
            <IconButton
              size="small"
              onClick={() => setMobileStep("calendar")}
              sx={{
                display: { xs: "inline-flex", md: "none" },
                mr: 0.5,
                ml: -0.5,
                color: "#6B7280",
                "&:hover": { bgcolor: "#F3F4F6", color: "#374151" },
              }}
            >
              <ArrowBackRoundedIcon fontSize="small" />
            </IconButton>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "0.92rem", sm: "0.96rem", md: "1rem" },
                color: "#111827",
                fontFamily: "var(--font-plusjakarta), sans-serif",
              }}
            >
              {DAY_NAME_SHORT[selectedDate.getDay()]}{" "}
              <Box component="span" sx={{ color: "#9CA3AF", fontWeight: 500 }}>
                {selectedDate.getDate()}
                {ordinalSuffix(selectedDate.getDate())}
              </Box>
            </Typography>
          </Box>

          {/* 12h/24h toggle — display format only, doesn't change booking payload */}
          <Box
            sx={{
              display: "flex",
              border: "1px solid #EDEDF1",
              borderRadius: "8px",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {[
              { label: "12h", value: false },
              { label: "24h", value: true },
            ].map((opt) => (
              <Box
                key={opt.label}
                onClick={() => setIs24h(opt.value)}
                sx={{
                  px: { xs: 1.1, md: 1.3 },
                  py: 0.4,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  userSelect: "none",
                  color: is24h === opt.value ? "#fff" : "#6B7280",
                  bgcolor: is24h === opt.value ? "#4C1D95" : "transparent",
                  transition: "background-color 0.15s ease, color 0.15s ease",
                  "&:hover": is24h !== opt.value ? { bgcolor: "#F3F4F6" } : {},
                }}
              >
                {opt.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            pr: 0.75,
            ...thinScrollbarSx,
          }}
        >
          {loadingSlots && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} sx={{ color: "#7C3AED" }} />
            </Box>
          )}

          {!loadingSlots && !error && slots.length === 0 && (
            <Typography sx={{ color: "#9CA3AF", fontSize: "0.88rem", textAlign: "center", py: 4 }}>
              No slots available for this day.
            </Typography>
          )}

          {error && (
            <Typography sx={{ color: "#EF4444", fontSize: "0.88rem", textAlign: "center", py: 2 }}>
              {error}
            </Typography>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {slots.map((slot) => (
              <Button
                key={slot.start}
                onClick={() => handleBook(slot)}
                disabled={booking}
                variant="outlined"
                fullWidth
                sx={{
                  borderColor: "#EDEDF1",
                  color: "#111827",
                  textTransform: "none",
                  fontFamily: "var(--font-outfit), sans-serif",
                  fontWeight: 600,
                  fontSize: { xs: "0.84rem", md: "0.88rem" },
                  borderRadius: "8px",
                  py: { xs: 0.9, md: 1.1 },
                  justifyContent: "center",
                  "&:hover": { bgcolor: "#4C1D95", borderColor: "#4C1D95", color: "#fff" },
                }}
              >
                {formatTimeLabel(slot.start, is24h)}
              </Button>
            ))}
          </Box>
        </Box>

        {booking && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 2 }}>
            <CircularProgress size={16} sx={{ color: "#7C3AED" }} />
            <Typography sx={{ fontSize: "0.85rem", color: "#6B7280" }}>Booking your meeting, please wait...</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GoogleSlotPicker;